import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation } from '@tanstack/react-query';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Mail, KeyRound } from 'lucide-react';
import { authApi } from './api';
import { ApiError } from '@/lib/api/errors';
import { OAuthButton } from './OAuthButton';
import { FlowBackground } from './FlowBackground';
import { WaveBackdrop } from './WaveBackdrop';
import { AuthFooter } from './AuthFooter';
import { LanguageMenu } from '@/components/layout/PrefsMenu';
import { emailSchema, passwordLoginSchema, type EmailInput, type PasswordLoginInput } from '@/lib/schemas/auth';
import { useAuthStore } from '@/lib/auth/authStore';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { FormField } from '@/components/forms/FormField';
import { toast } from '@/components/ui/Toast';
import { useT } from '@/lib/i18n/useT';
import { usePlanContext } from '@onxite/plan-sdk';
import { afterAuthPath, capturePendingPlan, peekPendingPlan, pendingPlanLabel, type PendingPlan } from '@/features/billing/pendingPlan';

type Mode = 'otp' | 'password';

export function LoginPage() {
  const [mode, setMode] = useState<Mode>('otp');
  const navigate = useNavigate();
  // D1: si llega con `?plan=…` (o lo dejó la página de precios), tras entrar
  // se reanuda el pago de ese plan en vez de caer en el inicio.
  const [searchParams] = useSearchParams();
  const [pendingPlan] = useState<PendingPlan | null>(() => capturePendingPlan(searchParams) ?? peekPendingPlan());
  const setPendingEmail = useAuthStore((s) => s.setPendingEmail);
  const setSession = useAuthStore((s) => s.setSession);
  const t = useT();
  const { reportPlanAlert } = usePlanContext();

  const otpForm = useForm<EmailInput>({ resolver: zodResolver(emailSchema) });
  const pwdForm = useForm<PasswordLoginInput>({ resolver: zodResolver(passwordLoginSchema) });

  // El código de acceso es sólo para cuentas existentes: si el correo no es de
  // nadie, el backend no manda nada y aquí se ofrece crear la cuenta.
  const [unknownEmail, setUnknownEmail] = useState<string | null>(null);
  const sendOtp = useMutation({
    mutationFn: (input: EmailInput) => authApi.sendOtp(input.email),
    onSuccess: (data, { email }) => {
      if (data.sent === false) {
        setUnknownEmail(email);
        otpForm.setError('email', { type: 'manual', message: t('auth.login.noAccount') });
        return;
      }
      setUnknownEmail(null);
      setPendingEmail(email);
      if (data.devHint) toast({ tone: 'info', title: t('auth.toast.devHint'), description: data.devHint });
      navigate('/auth/otp');
    },
    onError: () => toast({ tone: 'error', title: t('auth.toast.otpFailed') }),
  });

  const loginPwd = useMutation({
    mutationFn: (input: PasswordLoginInput) => authApi.login(input.email, input.password),
    onSuccess: (data) => {
      setSession({
        token: data.access_token,
        refreshToken: data.refresh_token,
        user: data.user,
        role: data.role,
      });
      reportPlanAlert((data as any).planAlert ?? null);
      navigate(afterAuthPath('/'));
    },
    onError: (err) => {
      // 409 = single-session conflict: the account is already signed in
      // somewhere else (heartbeat within 15 min). Surface a specific message
      // instead of the generic "invalid credentials" — the backend also
      // notified the active session about this attempt.
      if (err instanceof ApiError && err.status === 409) {
        toast({
          tone: 'error',
          title: t('auth.toast.alreadySignedIn'),
          description: t('auth.toast.alreadySignedInBody'),
        });
        return;
      }
      toast({ tone: 'error', title: t('auth.toast.invalidCredentials') });
    },
  });

  return (
    // `data-surface="night"` re-points the content tokens at the dark-surface
    // palette for this whole subtree. Required because FlowBackground paints a
    // navy backdrop on every theme, while three of the five themes ship dark
    // ink as their foreground — see the scope in styles/tokens.css.
    <div data-surface="night" className="relative grid min-h-screen lg:grid-cols-2">
      {/* Full-page wavy backdrop (sits behind both columns at -z-10).
          The right hero column is opaque on lg+ and covers it there, so the
          curves end up only visible behind the form on the left — exactly
          the brand reference. */}
      <FlowBackground />
      {/* Left — form. No column-wide veil anymore: the form sits inside a
          glass card that gives elevation just where it's needed and lets the
          dot-grid texture show through everywhere else. */}
      <div className="relative z-10 flex items-center justify-center px-3 py-8 sm:px-6 sm:py-12">
        <AuthFooter />
        <div
          className="w-full max-w-md rounded-2xl border border-white/[0.06] bg-[rgb(10_6_19/0.55)] p-5 shadow-[0_30px_80px_-20px_rgb(0_0_0/0.7)] backdrop-blur-xl sm:p-8"
          style={{
            boxShadow:
              '0 30px 80px -20px rgb(0 0 0 / 0.7), 0 0 0 1px color-mix(in srgb, var(--color-xk-accent) 8%, transparent) inset',
          }}
        >
          <div className="mb-8 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <img src="/xk-icon.svg" alt="Xotski" className="h-9 w-9 rounded-lg" />
              <span className="font-display text-lg font-bold tracking-tight">xotski</span>
            </div>
            {/* El idioma se elige ANTES de entrar: quien llega a una pantalla
                en el idioma equivocado no tiene otra forma de cambiarla. */}
            <LanguageMenu />
          </div>

          <h1 className="text-3xl font-bold">{t('auth.login.welcomeBack')}</h1>
          {pendingPlan && (pendingPlan.plan === 'xk1' || pendingPlan.plan === 'xk2') ? (
            <p
              data-testid="pending-plan"
              className="mt-3 inline-flex items-center gap-2 rounded-full border border-[var(--color-accent-20)] bg-[var(--color-accent-06)] px-3 py-1.5 text-xs font-medium text-[var(--color-accent-ink)]"
            >
              <span className="font-mono text-[10px] font-bold uppercase tracking-widest">{pendingPlanLabel(pendingPlan)}</span>
              {t('auth.login.pendingPlan', { plan: pendingPlanLabel(pendingPlan) })}
            </p>
          ) : null}
          <p className="mt-2 text-sm text-[var(--color-fg-muted)]">
            {t('auth.login.subtitle')}
          </p>

          <div className="mt-8 inline-flex gap-1 rounded-lg border border-[var(--color-border-default)] p-1">
            <button
              type="button"
              onClick={() => setMode('otp')}
              className={`rounded-md px-3 py-1.5 text-xs font-semibold transition-colors ${
                mode === 'otp'
                  ? 'bg-[var(--color-accent)] text-[var(--color-fg-on-accent)]'
                  : 'text-[var(--color-fg-muted)] hover:text-[var(--color-fg-primary)]'
              }`}
            >
              {t('auth.login.modeOtp')}
            </button>
            <button
              type="button"
              onClick={() => setMode('password')}
              className={`rounded-md px-3 py-1.5 text-xs font-semibold transition-colors ${
                mode === 'password'
                  ? 'bg-[var(--color-accent)] text-[var(--color-fg-on-accent)]'
                  : 'text-[var(--color-fg-muted)] hover:text-[var(--color-fg-primary)]'
              }`}
            >
              {t('auth.login.modePassword')}
            </button>
          </div>

          {mode === 'otp' ? (
            <form
              noValidate
              className="mt-6 space-y-4"
              onSubmit={otpForm.handleSubmit((v) => sendOtp.mutate(v))}
            >
              <FormField
                label={t('auth.login.emailLabel')}
                error={otpForm.formState.errors.email?.message}
              >
                <div className="relative">
                  <Mail className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[var(--color-fg-subtle)]" />
                  <Input
                    type="email"
                    autoFocus
                    placeholder="you@brand.com"
                    className="pl-9"
                    {...otpForm.register('email')}
                  />
                </div>
              </FormField>
              {unknownEmail ? (
                <Link
                  to={`/register?email=${encodeURIComponent(unknownEmail)}`}
                  data-testid="create-with-email"
                  className="-mt-2 block text-sm font-semibold text-[var(--color-accent-ink)] hover:underline"
                >
                  {t('auth.login.createWithEmail')}
                </Link>
              ) : null}
              <Button type="submit" loading={sendOtp.isPending} className="w-full" size="lg">
                {t('auth.login.continueWithEmail')}
              </Button>
            </form>
          ) : (
            <form
              noValidate
              className="mt-6 space-y-4"
              onSubmit={pwdForm.handleSubmit((v) => loginPwd.mutate(v))}
            >
              <FormField label={t('auth.login.email')} error={pwdForm.formState.errors.email?.message}>
                <Input type="email" autoComplete="email" {...pwdForm.register('email')} />
              </FormField>
              <FormField label={t('auth.login.password')} error={pwdForm.formState.errors.password?.message}>
                <div className="relative">
                  <KeyRound className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[var(--color-fg-subtle)]" />
                  <Input
                    type="password"
                    className="pl-9"
                    autoComplete="current-password"
                    {...pwdForm.register('password')}
                  />
                </div>
              </FormField>
              <Button type="submit" loading={loginPwd.isPending} className="w-full" size="lg">
                {t('auth.login.signIn')}
              </Button>
              <div className="text-right">
                <Link
                  to="/auth/forgot-password"
                  className="text-xs font-semibold text-[var(--color-accent-ink)] hover:underline"
                >
                  {t('auth.login.forgotPassword')}
                </Link>
              </div>
            </form>
          )}

          {/* OAuth social sign-in. Backend strategies are pre-configured
              (env vars + Passport in ox-platform/core); we just need to
              hand off to /auth/<provider> and let the exchange-code flow
              handle the redirect back to /auth/oauth-callback. */}
          <div className="mt-6">
            <div className="relative flex items-center">
              <div className="flex-grow border-t border-[var(--color-border-default)]" />
              <span className="mx-3 text-xs uppercase tracking-wider text-[var(--color-fg-subtle)]">
                {t('auth.login.orContinueWith') || 'or continue with'}
              </span>
              <div className="flex-grow border-t border-[var(--color-border-default)]" />
            </div>
            <div className="mt-4 grid grid-cols-2 gap-2">
              <OAuthButton provider="google" label="Google" />
              <OAuthButton provider="linkedin" label="LinkedIn" />
              <OAuthButton provider="facebook" label="Facebook" comingSoon />
              <OAuthButton provider="apple" label="Apple" comingSoon />
            </div>
          </div>

          <p className="mt-8 text-center text-sm text-[var(--color-fg-muted)]">
            {t('auth.login.newHere')}{' '}
            <Link to="/register" className="font-semibold text-[var(--color-accent-ink)] hover:underline">
              {t('auth.login.createAccount')}
            </Link>
            {' · '}
            <Link to="/pricing" className="text-[var(--color-fg-muted)] hover:text-[var(--color-fg-primary)]">
              {t('auth.login.seePlans')}
            </Link>
          </p>
        </div>
      </div>

      {/* Right — visual hero. Slightly transparent so the page's dot grid
          and glows bleed in from the left edge for cohesion, while the
          gradient still does the heavy lifting. */}
      <div
        className="relative z-10 hidden overflow-hidden lg:flex lg:items-center lg:justify-center lg:p-12"
        style={{
          background:
            'linear-gradient(135deg, color-mix(in srgb, var(--color-xk-navy) 92%, transparent) 0%, color-mix(in srgb, var(--color-xk-navy-mid) 95%, transparent) 60%, color-mix(in srgb, var(--color-xk-bg-deep, var(--color-xk-navy-mid)) 92%, transparent) 100%)',
        }}
      >
        {/* The same wave silhouettes from the XK monogram, scaled to fill
            the column and rendered at very low opacity. Reads as a brand
            watermark behind the logo, not as artwork. */}
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0 text-[var(--color-accent-ink)] opacity-[0.08]"
          style={{
            maskImage:
              'radial-gradient(ellipse 75% 70% at 50% 50%, black 30%, transparent 90%)',
            WebkitMaskImage:
              'radial-gradient(ellipse 75% 70% at 50% 50%, black 30%, transparent 90%)',
          }}
        >
          <WaveBackdrop />
        </div>
        {/* Inner accent glow — anchors the hero visually around the logo
            mark and echoes the corner glow from the left side. */}
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0"
          style={{
            background:
              'radial-gradient(ellipse 60% 55% at 50% 45%, color-mix(in srgb, var(--color-xk-accent) 18%, transparent) 0%, transparent 70%)',
          }}
        />
        <div className="max-w-md text-center">
          <img src="/xk-icon.svg" alt="Xotski" className="mb-6 inline-block h-20 w-20 rounded-2xl" />
          <h2 className="text-3xl font-bold">{t('auth.login.heroTitle')}</h2>
          <p className="mt-3 text-sm text-[var(--color-fg-muted)]">
            {t('auth.login.heroBody')}
          </p>
        </div>
      </div>
    </div>
  );
}

